import React, { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import useAdminToken from "../hooks/useAdminToken";
import CheckoutEditor, { DAYS } from "../components/checkout-editor";
import OrdersTable from "../components/orders-table";

/* Standalone editor for the offer on public/checkout/.
 *
 * Kept off /admin/tools so the two don't compete for the same screen — that
 * page is a dense bulk-edit table, this one is a form the marketing team edits
 * under time pressure. They share the passphrase gate via useAdminToken, so
 * verifying on either page covers both. */

export default function AdminCheckout() {
  const { token, verified, busy, verify, clear } = useAdminToken();
  const [keyInput, setKeyInput] = useState("");

  /* Tab and day live in the query string, not in state: a refresh mid-edit —
     or a link pasted to someone else — lands back on the same view instead of
     dropping to Orders / Day 1. Unknown values fall back rather than throw, so
     a hand-typed ?tab=nonsense still renders something. Replace, not push, so
     switching tabs doesn't fill the back button with history. */
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "offer" ? "offer" : "orders";
  const day = DAYS.some((d) => String(d.day) === searchParams.get("day"))
    ? Number(searchParams.get("day"))
    : 1;

  const setParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(key, String(value));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Publish lives in the header row but belongs to the editor's form state, so
  // the editor hands its action up here and reports what it is busy doing.
  const publishRef = useRef(null);
  const [editorStatus, setEditorStatus] = useState({
    publishing: false,
    loading: true,
  });
  const activeDay = DAYS.find((d) => d.day === day) || DAYS[0];

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      await verify(keyInput);
      setKeyInput("");
      toast.success("Verified");
    } catch (err) {
      toast.error(String((err && err.message) || err));
    }
  };

  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card bg-base-100 shadow w-full max-w-md">
          <div className="card-body">
            <h3 className="font-bold text-lg">Admin Verification</h3>
            <p className="py-2 text-sm">
              Enter the admin key to edit the checkout page.
            </p>
            <form onSubmit={handleVerify}>
              <input
                type="password"
                className="input input-bordered w-full"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Admin key"
                autoFocus
              />
              <div className="modal-action">
                <button className="btn btn-primary" disabled={busy}>
                  {busy && <span className="loading loading-spinner loading-xs" />}
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h1 className="text-2xl font-bold">Checkout Page</h1>
        {/* Wraps to its own full-width line on a phone rather than squeezing
            the button into whatever the title leaves over. */}
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <button className="btn btn-outline btn-error btn-sm" onClick={clear}>
            Re-verify
          </button>
        </div>
      </div>

      {/* One row: section tabs, then the day switch, then the actions pushed to
          the far end. Day switch and actions only under Edit offer — they mean
          nothing to the orders table, and a dead control invites a click that
          does nothing. */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div role="tablist" className="tabs tabs-boxed w-full sm:w-fit">
          <button
            role="tab"
            className={`tab flex-1 sm:flex-none ${tab === "orders" ? "tab-active" : ""}`}
            onClick={() => setParam("tab", "orders")}
          >
            Orders
          </button>
          <button
            role="tab"
            className={`tab flex-1 sm:flex-none ${tab === "offer" ? "tab-active" : ""}`}
            onClick={() => setParam("tab", "offer")}
          >
            Edit offer
          </button>
        </div>

        {tab === "offer" && (
          <>
            <div role="tablist" className="tabs tabs-boxed w-full sm:w-fit">
              {DAYS.map((d) => (
                <button
                  key={d.day}
                  role="tab"
                  type="button"
                  className={`tab flex-1 sm:flex-none ${d.day === day ? "tab-active" : ""}`}
                  aria-selected={d.day === day}
                  onClick={() => setParam("day", d.day)}
                  disabled={editorStatus.publishing}
                >
                  {d.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
              <a
                href={activeDay.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost btn-square"
                title={`View ${activeDay.label} (${activeDay.url})`}
                aria-label={`View ${activeDay.label}`}
              >
                {/* Eye — follows the selected day, so there is one button here
                    instead of one per day. */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </a>
              <button
                className="btn btn-primary flex-1 sm:flex-none"
                onClick={() => publishRef.current && publishRef.current()}
                disabled={editorStatus.publishing || editorStatus.loading}
              >
                {editorStatus.publishing && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                Publish {activeDay.label}
              </button>
            </div>
          </>
        )}
      </div>

      {tab === "orders" ? (
        <OrdersTable token={token} />
      ) : (
        <CheckoutEditor
          token={token}
          day={day}
          onStatusChange={setEditorStatus}
          publishRef={publishRef}
        />
      )}
    </div>
  );
}
